import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Collapse,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

import { ButtonTitleWithLoading } from '@vyce/core/src/components';
import { AppLinearProgress } from '@vyce/core/src/components/AppLinearProgress';
import { OnboardingStatusBase } from '@vyce/core/src/types';
import { formatUtcToLocalDateTime } from '@vyce/core/src/utils/dates';
import {
  AnyOnboarding,
  getModuleCompletionPercent,
  getOnboardingGroups,
  OnboardingGroup,
  OnboardingRow,
} from '@vyce/core/src/utils/onboardingStatus';

import useStyles from '../../styles';
import { BoxWrapper } from './BoxWrapper';

interface OnboardingDetailsProps {
  onboarding?: AnyOnboarding | null;
  // Takes the company to recompute. pay-app is scoped to one company and ignores the
  // argument; Legend passes the company whose section the button sits in.
  onRecomputeOnboarding?: (companyId?: string) => Promise<void>;
  recomputeOnboardingLoading?: boolean;
  // Which company's refresh is in flight, so only that button shows the spinner
  recomputingCompanyId?: string;
}

// A worker can sit in a dozen companies. The sections collapse, and past this height
// the list scrolls inside the tile instead of stretching the profile grid.
const LIST_MAX_HEIGHT = 420;

const useLocalStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {
      width: '100%',
      maxHeight: LIST_MAX_HEIGHT,
      overflowY: 'auto',
    },
    companyHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gridGap: theme.spacing(1),
      width: '100%',
      padding: theme.spacing(0.75, 1),
      borderRadius: 8,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    // Trading names run long — the name ellipsises and the status keeps its place
    companyName: {
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    companyBody: {
      marginLeft: theme.spacing(1.5),
      paddingLeft: theme.spacing(1.5),
      paddingTop: theme.spacing(1),
      borderLeft: `2px solid ${theme.palette.divider}`,
    },
  })
);

const getLastEvaluatedAt = (statuses: (OnboardingStatusBase | undefined)[]): string | undefined =>
  statuses
    .map(status => status?.evaluated_at)
    .filter((date): date is string => !!date)
    .sort()
    .pop();

const StatusLabel = ({ status }: { status?: OnboardingStatusBase }) => {
  const theme = useTheme();
  const percent = getModuleCompletionPercent(status ?? {});

  if (status?.is_complete) {
    return (
      <Box color={theme.palette.success.main} flexShrink={0}>
        <Typography variant="subtitle2">Complete</Typography>
      </Box>
    );
  }

  if (typeof percent === 'number') {
    return (
      <Box color={theme.palette.warning.main} flexShrink={0}>
        <Typography variant="subtitle2">{percent}%</Typography>
      </Box>
    );
  }

  return (
    <Box color={theme.palette.text.secondary} flexShrink={0}>
      <Typography variant="subtitle2">Not assessed</Typography>
    </Box>
  );
};

const StatusRow = ({ row }: { row: OnboardingRow }) => {
  const classes = useStyles();
  const percent = getModuleCompletionPercent(row.status);
  const missing = (row.status.missing_required ?? [])
    .map(item => item.label || item.key)
    .filter(Boolean)
    .join(', ');

  return (
    <Box width="100%" marginBottom={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={8}>
        <Typography className={classes.bold}>{row.label}:</Typography>
        <StatusLabel status={row.status} />
      </Box>
      {!row.status.is_complete && typeof percent === 'number' && (
        <AppLinearProgress variant="determinate" value={percent} />
      )}
      {!row.status.is_complete && missing && (
        <Typography color="textSecondary" variant="caption" component="div">
          Missing: {missing}
        </Typography>
      )}
    </Box>
  );
};

const RefreshButton = ({
  loading,
  disabled,
  onClick,
}: {
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) => (
  <Button
    color="primary"
    variant="outlined"
    size="small"
    style={{ minWidth: 120 }}
    disabled={disabled}
    onClick={onClick}>
    <ButtonTitleWithLoading title="Refresh status" loaderVariant="primary" loading={loading} />
  </Button>
);

const CompanySection = ({
  group,
  expanded,
  onToggle,
  onRefresh,
  refreshing,
  refreshDisabled,
}: {
  group: OnboardingGroup;
  expanded: boolean;
  onToggle: () => void;
  onRefresh?: () => void;
  refreshing: boolean;
  refreshDisabled: boolean;
}) => {
  const classes = useLocalStyles();
  const theme = useTheme();
  const lastEvaluatedAt = getLastEvaluatedAt([...group.rows.map(row => row.status), group.status]);

  return (
    <Box width="100%">
      <Box
        className={classes.companyHeader}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}>
        <Box display="flex" alignItems="center" gridGap={8} minWidth={0}>
          {expanded ? (
            <FiChevronUp size={18} color={theme.palette.text.secondary} />
          ) : (
            <FiChevronDown size={18} color={theme.palette.text.secondary} />
          )}
          <Typography className={classes.companyName} title={group.label}>
            {group.label}
          </Typography>
        </Box>

        <StatusLabel status={group.status} />
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box className={classes.companyBody}>
          {group.rows.map(row => (
            <StatusRow key={row.key} row={row} />
          ))}

          {lastEvaluatedAt && (
            <Typography color="textSecondary" variant="caption" component="div">
              Last checked: {formatUtcToLocalDateTime(lastEvaluatedAt)}
            </Typography>
          )}

          {onRefresh && (
            <Box marginTop={1} marginBottom={1}>
              <RefreshButton loading={refreshing} disabled={refreshDisabled} onClick={onRefresh} />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export const OnboardingDetails = ({
  onboarding,
  onRecomputeOnboarding,
  recomputeOnboardingLoading = false,
  recomputingCompanyId,
}: OnboardingDetailsProps) => {
  const classes = useLocalStyles();
  const theme = useTheme();
  const groups = useMemo(() => getOnboardingGroups(onboarding), [onboarding]);
  // Legend groups by company; pay-app is one company and renders its modules flat.
  const groupedByCompany = groups.some(group => !!group.label);
  const [expandedKeys, setExpandedKeys] = useState<string[]>();

  // A single company opens straight away; a longer list starts collapsed so every
  // company is visible at a glance. Seeded once, so a refresh keeps what the user opened.
  useEffect(() => {
    if (!groups.length) {
      return;
    }
    setExpandedKeys(prev => prev ?? (groups.length === 1 ? groups.map(group => group.key) : []));
  }, [groups]);

  const openKeys = expandedKeys ?? [];
  const allExpanded = !!groups.length && groups.every(group => openKeys.includes(group.key));

  const toggleGroup = (key: string) =>
    setExpandedKeys(prev => {
      const current = prev ?? [];
      return current.includes(key) ? current.filter(item => item !== key) : [...current, key];
    });

  const flatRows = groupedByCompany ? [] : groups[0]?.rows ?? [];
  const flatLastEvaluatedAt = getLastEvaluatedAt(flatRows.map(row => row.status));

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Typography color="textSecondary" variant="h6">
        Onboarding
      </Typography>

      <BoxWrapper>
        <Box flex="1 0 auto" width="100%" minWidth={0}>
          {!groups.length && (
            <Box color={theme.palette.text.secondary}>
              <Typography variant="subtitle2">Not assessed yet</Typography>
            </Box>
          )}

          {groupedByCompany ? (
            <>
              {groups.length > 1 && (
                <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={1}>
                  <Typography color="textSecondary" variant="caption">
                    {groups.length} companies
                  </Typography>

                  <Button
                    color="primary"
                    size="small"
                    onClick={() => setExpandedKeys(allExpanded ? [] : groups.map(group => group.key))}>
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                  </Button>
                </Box>
              )}

              <Box className={classes.list}>
                {groups.map(group => (
                  <CompanySection
                    key={group.key}
                    group={group}
                    expanded={openKeys.includes(group.key)}
                    onToggle={() => toggleGroup(group.key)}
                    onRefresh={
                      onRecomputeOnboarding ? () => onRecomputeOnboarding(group.companyId) : undefined
                    }
                    refreshing={recomputeOnboardingLoading && recomputingCompanyId === group.companyId}
                    refreshDisabled={recomputeOnboardingLoading}
                  />
                ))}
              </Box>
            </>
          ) : (
            <>
              {flatRows.map(row => (
                <StatusRow key={row.key} row={row} />
              ))}

              {flatLastEvaluatedAt && (
                <Typography color="textSecondary" variant="caption" component="div">
                  Last checked: {formatUtcToLocalDateTime(flatLastEvaluatedAt)}
                </Typography>
              )}
            </>
          )}
        </Box>

        {!groupedByCompany && onRecomputeOnboarding && (
          <Box width="100%" marginTop={1}>
            <RefreshButton
              loading={recomputeOnboardingLoading}
              disabled={recomputeOnboardingLoading}
              onClick={() => onRecomputeOnboarding()}
            />
          </Box>
        )}
      </BoxWrapper>
    </Box>
  );
};
